import { ChipTab } from "@/ui/wx";
import type { OhlcCandle } from "@/api/market";
import { fmtUsd } from "@/lib/format";

export function PairStats({
  last,
  high,
  low,
  volume,
  indexPrice,
}: {
  last: string;
  high: string;
  low: string;
  volume: string;
  indexPrice: string;
}) {
  const cells = [
    { k: "بالاترین", v: high },
    { k: "پایین‌ترین", v: low },
    { k: "حجم", v: volume },
    { k: "شاخص", v: indexPrice },
    { k: "آخرین", v: last },
  ];
  return (
    <div className="wx-stats-grid">
      {cells.map((c) => (
        <div key={c.k} className="wx-stat">
          <span className="muted">{c.k}</span>
          <b className="ltr">{c.v}</b>
        </div>
      ))}
    </div>
  );
}

function closesOf(candles: OhlcCandle[]): number[] {
  const out: number[] = [];
  for (const c of candles) {
    const n = Number(String(c.close).replace(/,/g, ""));
    if (Number.isFinite(n) && n > 0) out.push(n);
  }
  return out;
}

function polyline(closes: number[]): string {
  if (closes.length < 2) return "";
  const min = Math.min(...closes);
  const max = Math.max(...closes);
  const span = max - min || max * 0.01 || 1;
  return closes
    .map((v, i) => {
      const x = (i / (closes.length - 1)) * 100;
      const y = 72 - ((v - min) / span) * 56;
      return `${x},${Math.max(6, Math.min(74, y))}`;
    })
    .join(" ");
}

export function ChartTabs({
  tab,
  onChange,
  last,
  candles,
  source,
  emptyReason,
}: {
  tab: "nanom" | "global" | "depth";
  onChange: (t: "nanom" | "global" | "depth") => void;
  last: string;
  candles?: OhlcCandle[];
  source?: string;
  emptyReason?: string | null;
}) {
  const closes = closesOf(candles || []);
  const showSeries = tab === "nanom" && closes.length >= 2;
  const line = showSeries ? polyline(closes) : "";
  const lastClose = closes.length ? closes[closes.length - 1] : null;
  const firstClose = closes.length ? closes[0] : null;
  const up = lastClose != null && firstClose != null ? lastClose >= firstClose : true;

  return (
    <div className="wx-chart" dir="rtl">
      <div className="wx-mode-tabs">
        <ChipTab active={tab === "nanom"} onClick={() => onChange("nanom")}>
          نانوم
        </ChipTab>
        <ChipTab active={tab === "global"} onClick={() => onChange("global")}>
          جهانی
        </ChipTab>
        <ChipTab active={tab === "depth"} onClick={() => onChange("depth")}>
          عمق
        </ChipTab>
      </div>
      {tab !== "nanom" ? (
        <div className="wx-chart-empty muted">—</div>
      ) : showSeries ? (
        <svg className="wx-chart-svg" viewBox="0 0 100 80" preserveAspectRatio="none" aria-hidden="true">
          <polyline
            fill="none"
            stroke={up ? "var(--buy)" : "var(--sell)"}
            strokeWidth="1.5"
            points={line}
          />
          <line x1="0" y1="40" x2="100" y2="40" stroke="var(--line)" strokeWidth="0.5" />
        </svg>
      ) : (
        <div className="wx-chart-empty muted">{emptyReason ? "—" : "—"}</div>
      )}
      <div className="muted wx-chart-cap ltr">
        {lastClose != null ? fmtUsd(lastClose) : last}
        {source ? ` · ${source}` : ""}
        {closes.length ? ` · n=${closes.length}` : ""}
      </div>
    </div>
  );
}
